// spelldance client entry.
//
// Owns: the canvas viewport, the network worker, the input pump, the
// prediction engine, and the lightweight status HUD in the top-right
// corner.
//
// Data flow for one input tick:
//   InputPump (30 Hz) → sender callback below →
//      ├─ PredictionEngine.recordInput → predicted self advances
//      └─ worker.postMessage(input) → wasm encode → datagram
//
// Data flow for one snapshot:
//   server → worker.network → wasm decode → main.ts onmessage →
//      ├─ Viewport.setSnapshot (interp buffer + non-self render)
//      └─ PredictionEngine.reconcile(your row, lastInputTick)

import { BuildPrediction, packCellKey } from "./buildPrediction";
import { InputPump, TICK_INTERVAL_MS } from "./input";
import { PredictionEngine } from "./prediction";
import { Viewport } from "./renderer/viewport";
import {
  BUILD_ACTION_BREAK,
  BUILD_ACTION_NONE,
  BUILD_ACTION_PLACE,
  type WorkerToMain,
} from "./worker-protocol";

const canvas = document.getElementById("game-canvas");
if (!(canvas instanceof HTMLCanvasElement)) {
  throw new Error("missing #game-canvas in index.html");
}
const statusEl = document.getElementById("status");
if (statusEl === null) {
  throw new Error("missing #status element in index.html");
}
const matchBannerEl = document.getElementById("match-banner");
if (matchBannerEl === null) {
  throw new Error("missing #match-banner element in index.html");
}
const titleScreenEl = document.getElementById("title-screen");
const usernameInputEl = document.getElementById(
  "username-input",
) as HTMLInputElement | null;
const enterButtonEl = document.getElementById(
  "enter-button",
) as HTMLButtonElement | null;
const loadingScreenEl = document.getElementById("loading-screen");
const loadingLabelEl = document.getElementById("loading-label");
const loadingSublabelEl = document.getElementById("loading-sublabel");
if (
  titleScreenEl === null ||
  usernameInputEl === null ||
  enterButtonEl === null ||
  loadingScreenEl === null ||
  loadingLabelEl === null ||
  loadingSublabelEl === null
) {
  throw new Error("missing title/loading screen elements in index.html");
}

// MatchPhase wire enum mirror. Ended is dead-code (the server no
// longer transitions into it — matches persist as long as ≥ 1 player
// is connected) but kept for backward-compat with the wire format.
const MATCH_PHASE_WAITING = 0;
const MATCH_PHASE_ACTIVE = 1;

const viewport = new Viewport(canvas);
const predictor = new PredictionEngine();
const buildPredictor = new BuildPrediction();

/** Latest snapshot's wall set, packed `(x,y)` keys. Updated on every
 *  snapshot — used by `tryPlace`/`tryBreak` to gate predictions against
 *  what the server currently shows. Allocated once and rebuilt in place
 *  to avoid per-snapshot churn. */
const latestServerWalls = new Set<number>();

/** Combined wall set for client-side collision: server walls + pending
 *  places - pending breaks (applied in tick order). Driven by
 *  `rebuildCollisionWalls`. The PredictionEngine's sweep reads through
 *  a closure over this set, so any in-place mutation is visible to the
 *  very next `recordInput` call without re-wiring. */
const collisionWalls = new Set<number>();
function rebuildCollisionWalls(): void {
  buildPredictor.displayedWallsInto(latestServerWalls, collisionWalls);
}
predictor.setWallLookup((cx, cy) => collisionWalls.has(packCellKey(cx, cy)));
// Render-position resolver: predicted + decaying catch-up offset.
// In the steady state the offset is zero, so this returns the
// predicted position EXACTLY — zero input lag, no easing. The
// smoothing only kicks in to absorb server disagreements.
// Routing through a closure means we can switch the source later
// (e.g. for spectator camera) without touching the viewport.
viewport.setSelfResolver(() =>
  predictor.getRenderPosition(performance.now(), renderAlpha),
);
viewport.start();

// ---------------------------------------------------------------------------
// Single-clock fixed-timestep driver (the arras/diep "Fix Your Timestep"
// model). The viewport's vsync ticker calls `frameHook` once per painted
// frame. We accumulate real elapsed time and drain it in fixed 33.333 ms
// `input.step()` chunks (the sim runs at a rock-steady 30 Hz no matter
// the refresh rate), then expose the leftover fraction as `renderAlpha`
// so the predicted self lerps smoothly between sim steps. Because the
// SAME clock both steps the sim and paints the frame, there's no
// setTimeout-vs-vsync beat — that beat was the micro-jitter.
// ---------------------------------------------------------------------------
let ticking = false; // true between "connected" and "disconnected"
let lastFrameMs: number | null = null;
let accumulatorMs = 0;
let renderAlpha = 0; // [0,1) — fraction into the current sim step
/** Clamp a single frame's dt so a long stall (tab throttle, GC pause)
 *  can't queue a burst of catch-up steps. */
const MAX_FRAME_DT = TICK_INTERVAL_MS * 5;
const MAX_STEPS_PER_FRAME = 5;
function frameHook(nowMs: number): void {
  if (!ticking) {
    lastFrameMs = nowMs;
    return;
  }
  if (lastFrameMs === null) {
    lastFrameMs = nowMs;
    return;
  }
  let dt = nowMs - lastFrameMs;
  lastFrameMs = nowMs;
  if (dt > MAX_FRAME_DT) dt = MAX_FRAME_DT;
  accumulatorMs += dt;
  let steps = 0;
  while (accumulatorMs >= TICK_INTERVAL_MS && steps < MAX_STEPS_PER_FRAME) {
    input.step();
    accumulatorMs -= TICK_INTERVAL_MS;
    steps++;
  }
  // Anti-spiral: if we hit the step cap with time still owed, drop the
  // backlog rather than carrying it into the next frame forever.
  if (accumulatorMs >= TICK_INTERVAL_MS) accumulatorMs = 0;
  renderAlpha = accumulatorMs / TICK_INTERVAL_MS;
}

const worker = new Worker(
  new URL("./workers/network.worker.ts", import.meta.url),
  { type: "module" },
);

// --- Join lifecycle ---------------------------------------------------
//
// The worker boots immediately (it downloads + instantiates the wasm
// bundle in the background) but we don't open the WebSocket until the
// user clicks ENTER GAME. The server itself is auto-stopped on Fly
// when nobody's connected — the WebSocket connect wakes it (5–10 s
// cold start) which is hidden behind the JOINING overlay.
//
// Client-side load states:
//
//   'title'   — title screen shown, no connect attempted yet
//   'joining' — Enter clicked. WS connecting + waiting for first
//               snapshot. JOINING overlay visible. This also covers
//               Fly's cold-start window.
//   'starting'— First snapshot landed (or Waiting→Active transition
//               just fired). GAME STARTING overlay for ~1.5 s.
//   'in-game' — Overlay hidden. Player is fully in.
//
// Server transitions Waiting→Active when ≥ MIN_PLAYERS_TO_START alive
// (currently 1, so a solo join creates the game immediately).

type LoadState = "title" | "joining" | "starting" | "in-game";

let workerReady = false;
let userEntered = false;
let username = "guest";
let loadState: LoadState = "title";
/** `performance.now()` epoch at which `starting` should flip to
 *  `in-game`. Set when entering the `starting` state. */
let startingUntilMs = 0;
const STARTING_FLASH_MS = 1500;
/** Most recent match phase from a snapshot, for edge detection on
 *  Waiting→Active so we can fire the GAME STARTING flash. */
let lastSeenMatchPhase: number | null = null;

function setLoadingText(label: string, sublabel: string): void {
  loadingLabelEl!.textContent = label;
  loadingSublabelEl!.textContent = sublabel;
}

function applyLoadState(): void {
  switch (loadState) {
    case "title":
      // Title screen visible, loading overlay hidden.
      loadingScreenEl!.classList.remove("show");
      titleScreenEl!.classList.remove("hidden");
      break;
    case "joining":
      titleScreenEl!.classList.add("hidden");
      loadingScreenEl!.classList.remove("hidden");
      loadingScreenEl!.classList.add("show");
      setLoadingText("JOINING", "connecting to server");
      break;
    case "starting":
      titleScreenEl!.classList.add("hidden");
      loadingScreenEl!.classList.remove("hidden");
      loadingScreenEl!.classList.add("show");
      setLoadingText("GAME STARTING", "stand by");
      break;
    case "in-game":
      titleScreenEl!.classList.add("hidden");
      loadingScreenEl!.classList.add("hidden");
      loadingScreenEl!.classList.remove("show");
      break;
  }
}

function setLoadState(next: LoadState): void {
  if (loadState === next) return;
  loadState = next;
  if (next === "starting") {
    startingUntilMs = performance.now() + STARTING_FLASH_MS;
  }
  applyLoadState();
}

function maybeConnect(): void {
  if (!workerReady || !userEntered) return;
  worker.postMessage({
    type: "connect",
    url: import.meta.env.VITE_SERVER_URL ?? "ws://localhost:8080",
  });
}

function enterGame(): void {
  if (userEntered) return; // double-click guard
  const raw = usernameInputEl!.value.trim();
  // Server doesn't carry names on the wire yet; keep it local for the
  // HUD until a player-name field lands in the protocol. Empty input
  // falls back to "guest" so the button is always usable.
  username = raw.length > 0 ? raw : "guest";
  userEntered = true;
  // Disable so frantic clickers can't double-fire connect. Re-enable
  // would only make sense if we let users back to the title screen.
  enterButtonEl!.disabled = true;
  setLoadState("joining");
  maybeConnect();
}

enterButtonEl.addEventListener("click", enterGame);
usernameInputEl.addEventListener("keydown", (e) => {
  if (e.key === "Enter") enterGame();
});
// Autofocus the input so the user can just type-and-press-Enter.
usernameInputEl.focus();

const input = new InputPump(
  canvas,
  (mx, my) => viewport.aimAngleFromMouse(mx, my),
  (mx, my) => viewport.cursorWorldFromMouse(mx, my),
  (msg) => {
    // Predict locally BEFORE sending: the prediction advances at the
    // same tick the input gets shipped, so the next render frame sees
    // the new predicted position even if the datagram is dropped.
    predictor.recordInput({
      tick: msg.tick,
      moveX: msg.moveX,
      moveY: msg.moveY,
    });

    // Build prediction. The input pump now ships a LIST of cells per
    // tick (every cell the cursor crossed since the last send), so we
    // predict each one. `tryPlaceCell` / `tryBreakCell` apply the same
    // gates the server would and short-circuit on dupes; safe to call
    // for every cell in the batch. We refresh the collision-wall set
    // ONCE at the end so all the new pending walls land in collision
    // for the next tick's movement, mirroring the server schedule
    // (apply_movement of tick N+1 sees walls placed during tick N).
    if (msg.buildAction !== BUILD_ACTION_NONE && msg.buildCells.length > 0) {
      const playerPos = predictor.getPredicted();
      for (let i = 0; i < msg.buildCells.length; i += 2) {
        const cellX = msg.buildCells[i]!;
        const cellY = msg.buildCells[i + 1]!;
        if (msg.buildAction === BUILD_ACTION_PLACE) {
          const availableMana = hud.yourMana + buildPredictor.predictedManaDelta();
          buildPredictor.tryPlaceCell(
            msg.tick,
            cellX,
            cellY,
            playerPos,
            availableMana,
            latestServerWalls,
          );
        } else if (msg.buildAction === BUILD_ACTION_BREAK) {
          buildPredictor.tryBreakCell(
            msg.tick,
            cellX,
            cellY,
            playerPos,
            latestServerWalls,
          );
        }
      }
      rebuildCollisionWalls();
    }

    worker.postMessage(msg);
  },
  // Notify on build-mode toggle so the HUD updates immediately.
  () => paintStatus(),
);

// Live aim resolver for the renderer. The snapshot's `facings[self]`
// echoes back at 20 Hz and would make the self aim ray stair-step;
// pulling from the input pump's tracked mouse position every render
// frame keeps it glued to the cursor with no lag.
viewport.setAimResolver(() => {
  const i = input.peek();
  return viewport.aimAngleFromMouse(i.mouseX, i.mouseY);
});

// Drive the fixed-timestep accumulator off the viewport's vsync ticker.
// Now that `input` exists, hand the hook to the renderer.
viewport.setFrameHook(frameHook);

// DIAGNOSTIC HUD: surface reconcile internals so we can see whether the
// correction path is a live jitter source. `drift` = current renderOffset
// magnitude (the visible camera wobble); `corr` = reconciles that
// exceeded the deadzone and actually snapped; `dz` = reconciles the
// deadzone absorbed. Stand still and watch: if `corr` climbs and `drift`
// is nonzero/bouncing, reconcile is the cause. If `corr` stays flat and
// `drift` ~0 but it still jitters, the cause is in the render path.
viewport.setDebugResolver(() => {
  const d = predictor.debugInfo();
  return `drift ${d.drift.toFixed(4)} corr ${d.corrections} dz ${d.deadzoneHits}`;
});

// Pending wall overlay resolver. Pulled every RAF frame so a click
// shows the placed wall on the very next paint, before the snapshot
// containing it has arrived. Empty additions+removals = no in-flight
// predictions; renderer falls back to server walls untouched.
viewport.setBuildOverlayResolver(() => ({
  additions: buildPredictor.predictedAdditions(),
  removals: buildPredictor.predictedRemovals(),
}));

// Build-cursor resolver: drives the placement preview box + break
// circle + radius ring. Pulled every RAF frame (60–144 Hz) instead of
// the old 10 Hz push from `paintStatus`, so the cell outline tracks
// the mouse with zero perceived lag. Pure local read — `input.peek()`
// is just the InputPump's last-recorded mouse position from native
// mousemove events, no server round-trip involved.
viewport.setBuildCursorResolver(() => {
  const i = input.peek();
  return {
    active: i.buildMode,
    mouseCanvasX: i.mouseX,
    mouseCanvasY: i.mouseY,
    breakHeld: i.breakHeld,
  };
});

// Status HUD. Refreshed at 10 Hz — paint cost is tiny but no need to
// rebuild the string at full frame rate. Also drives the viewport's
// build-overlay snapshot so the renderer sees the current mouse + mode.
const hud = {
  conn: "booting",
  workerLog: "",
  lastTick: "",
  yourMana: 0,
  matchPhase: MATCH_PHASE_WAITING,
  matchSinceTick: 0,
  winnerId: 0,
  serverTick: 0,
  ping: 0, // most recent input→snapshot round-trip, ms
};

// --- Ping tracking -----------------------------------------------------
//
// True application-level ping/pong over a WebSocket text frame. Worker
// sends `p:<perf_now>` every 1 s; server's connection task echoes it
// straight from the I/O thread (NOT through the game loop) so the RTT
// is pure network time — no tick quantization, no 33/67 ms snapshot
// cadence jitter from the previous input-ack approach.

/** Rolling 10-second history of measured RTTs. New samples push to the
 *  back; entries older than the window get dropped on each insert. */
const pingHistory: Array<{ time: number; rtt: number }> = [];
const PING_WINDOW_MS = 10_000;

function recordPing(rttMs: number): void {
  const now = performance.now();
  pingHistory.push({ time: now, rtt: rttMs });
  while (pingHistory.length > 0 && pingHistory[0]!.time < now - PING_WINDOW_MS) {
    pingHistory.shift();
  }
  hud.ping = rttMs;
}

/** Average over the last `PING_WINDOW_MS`. Returns 0 if no samples. */
function averagePing(): number {
  if (pingHistory.length === 0) return 0;
  let sum = 0;
  for (const e of pingHistory) sum += e.rtt;
  return sum / pingHistory.length;
}

function paintMatchBanner(): void {
  let text = "";
  // Only Waiting renders a banner now — the win-cycle (Ended phase)
  // is gone, matches stay Active for as long as a player is connected.
  // While the GAME STARTING flash is showing the loading overlay
  // takes priority anyway.
  if (hud.matchPhase === MATCH_PHASE_WAITING) {
    text = "WAITING FOR PLAYERS";
  }
  if (text === "") {
    matchBannerEl!.classList.remove("show");
    matchBannerEl!.textContent = "";
  } else {
    matchBannerEl!.classList.add("show");
    matchBannerEl!.textContent = text;
  }
}
function tickLoadState(): void {
  // Single state-machine sweep, called from the 10 Hz HUD loop. The
  // GAME STARTING overlay self-expires; everything else is event-
  // driven from worker messages.
  if (loadState === "starting" && performance.now() >= startingUntilMs) {
    setLoadState("in-game");
  }
}

function paintStatus(): void {
  tickLoadState();
  // Username is local-only for now (no wire field). Displaying it here
  // is mostly cosmetic — confirms the value the player typed at the
  // title screen, until a `Hello` packet adds it to the protocol.
  const parts: string[] = [`player: ${username}`, `net: ${hud.conn}`];
  // FPS + ping meter line. FPS comes from the viewport's per-frame
  // counter (so it reflects the actual RAF rate, capped by display
  // refresh). Ping is input→snapshot round-trip — server tick + AOI
  // emit cadence inflate it by ~40 ms over pure network RTT, but the
  // value is stable enough to track network conditions in real time.
  const fps = viewport.getFps();
  parts.push(
    `fps: ${fps.toFixed(0)}  ping: ${hud.ping.toFixed(0)}ms  ping(10s): ${averagePing().toFixed(0)}ms`,
  );
  if (hud.lastTick !== "") parts.push(hud.lastTick);
  const i = input.peek();
  // Predicted mana = authoritative `your_mana` + the net delta from
  // any pending place/break actions. Negative delta = pre-deducted
  // mana for in-flight placements; positive = pre-refunded breaks.
  // Reconciles silently when the server's next snapshot lands.
  const manaDelta = buildPredictor.predictedManaDelta();
  const displayedMana = Math.max(0, hud.yourMana + manaDelta);
  const manaTag = manaDelta === 0
    ? `mana ${displayedMana}`
    : `mana ${displayedMana} (server ${hud.yourMana}${manaDelta >= 0 ? "+" : ""}${manaDelta})`;
  parts.push(`mode: ${i.buildMode ? "BUILD" : "combat"}  ${manaTag}`);
  if (i.buildMode) {
    parts.push(
      `input: mv ${i.moveX},${i.moveY}  ${i.placeHeld ? "PLACE" : "     "}  ${i.breakHeld ? "BREAK" : "     "}`,
    );
  } else {
    parts.push(
      `input: mv ${i.moveX},${i.moveY}  ${i.fire ? "FIRE" : "    "}  ${i.useSkill ? "SKILL" : "     "}`,
    );
  }
  const pred = predictor.getPredicted();
  const dbg = predictor.debugInfo();
  parts.push(
    `pred: ${pred.x.toFixed(2)},${pred.y.toFixed(2)}  ack=${dbg.lastAck}  queued=${dbg.historyLen}`,
  );
  // `drift` is the magnitude of the still-decaying post-reconcile
  // visual offset. Zero = no correction in flight. Sustained > 0.05 =
  // the server is correcting you often (bad net, or a real bug).
  parts.push(`drift: ${dbg.drift.toFixed(3)} cells`);
  // `vel` is the live velocity vector — should ramp 0 → max while
  // holding a direction, snap to ~0 in one tick on counter-strafe.
  parts.push(`vel: ${dbg.velX.toFixed(3)},${dbg.velY.toFixed(3)}`);
  // `pendBuild` exposes the build-prediction queue. `pend` ticks down
  // ~50-100 ms after a click as the server's snapshot acks it. `own`
  // is the count of cells we've confirmed are ours.
  const bdbg = buildPredictor.debugInfo();
  parts.push(`pendBuild: ${bdbg.pending} (own ${bdbg.owned})`);
  if (hud.workerLog !== "") parts.push(hud.workerLog);
  statusEl!.textContent = parts.join("\n");

  // Match banner: also at 10 Hz, since the countdown is in whole seconds.
  paintMatchBanner();
}
window.setInterval(paintStatus, 100);
paintStatus();

worker.onmessage = (event: MessageEvent<WorkerToMain>) => {
  const msg = event.data;
  switch (msg.type) {
    case "ready":
      // The connect itself is deferred to the title screen's ENTER GAME
      // click. `maybeConnect()` short-circuits while `userEntered`
      // is still false. Vite injects `import.meta.env.VITE_SERVER_URL`
      // at build time; the worker hands the URL to
      // `new WebSocket(url)` directly. Scheme is `ws://` (local) or
      // `wss://` (prod).
      hud.conn = userEntered
        ? `worker ready (${msg.version}) — connecting…`
        : `worker ready (${msg.version}) — waiting for ENTER GAME`;
      workerReady = true;
      maybeConnect();
      return;
    case "connected":
      hud.conn = "connected";
      viewport.setConnected();
      input.start();
      // Arm the fixed-timestep driver. Reset the accumulator so the
      // first post-connect frame doesn't drain a backlog of steps.
      ticking = true;
      lastFrameMs = null;
      accumulatorMs = 0;
      renderAlpha = 0;
      return;
    case "disconnected":
      hud.conn = `disconnected: ${msg.reason}`;
      viewport.setDisconnected(msg.reason);
      input.stop();
      ticking = false;
      // Wipe predicted history — stale inputs would replay against the
      // next session's spawn point and put self in the wrong place,
      // and stale wall predictions would paint phantom cells on reconnect.
      predictor.reset();
      buildPredictor.reset();
      latestServerWalls.clear();
      collisionWalls.clear();
      pingHistory.length = 0;
      hud.ping = 0;
      // Disconnect always returns the user to the title screen so they
      // can re-enter (and pick a new name if they want). Re-enable the
      // ENTER button + clear the gating flags.
      userEntered = false;
      enterButtonEl!.disabled = false;
      lastSeenMatchPhase = null;
      setLoadState("title");
      return;
    case "snapshot":
      hud.lastTick = `tick ${msg.tick}  entities ${msg.count}  walls ${msg.wallsCount}  yourId=${msg.yourId}`;
      hud.yourMana = msg.yourMana;
      hud.matchPhase = msg.matchPhase;
      hud.matchSinceTick = msg.matchSinceTick;
      hud.winnerId = msg.winnerId;
      hud.serverTick = msg.tick;
      viewport.setSnapshot(msg);

      // Loading lifecycle hooks driven by snapshot data:
      //   1. First snapshot ever → leave 'joining'. If we landed
      //      mid-match (Active or Ended) flash GAME STARTING briefly,
      //      otherwise go straight in-game (Waiting phase's banner
      //      already communicates "waiting for players").
      //   2. Phase transition Waiting → Active → flash GAME STARTING.
      if (loadState === "joining") {
        if (msg.matchPhase === MATCH_PHASE_WAITING) {
          // We're alone or sub-quorum; the banner takes over from here.
          setLoadState("in-game");
        } else {
          setLoadState("starting");
        }
      } else if (
        lastSeenMatchPhase === MATCH_PHASE_WAITING &&
        msg.matchPhase === MATCH_PHASE_ACTIVE &&
        loadState === "in-game"
      ) {
        // Server just flipped Waiting → Active. Fire the GAME STARTING
        // flash; the per-frame timer below kicks us back to in-game.
        setLoadState("starting");
      }
      lastSeenMatchPhase = msg.matchPhase;

      // Rebuild the latest-walls index. Reused by the build prediction
      // for empty-cell + ownership checks. Cheap — wall counts are
      // bounded by AOI (~hundreds), and we'd be paying to construct
      // this set inside `viewport.setSnapshot` anyway.
      latestServerWalls.clear();
      for (let i = 0; i < msg.wallsCount; i++) {
        latestServerWalls.add(packCellKey(msg.wallXs[i]!, msg.wallYs[i]!));
      }
      // Drop acked place/break actions and update tracked ownership.
      buildPredictor.reconcile(msg.lastInputTick, latestServerWalls);
      // Collision set must reflect the freshly-acked snapshot before
      // `predictor.reconcile` replays unacked inputs — those replays
      // use the wallLookup that reads through `collisionWalls`.
      rebuildCollisionWalls();

      // Find self in the snapshot and reconcile predicted state. The
      // server's AOI cull always includes self (distance 0 < AOI
      // radius), so this lookup should always succeed once the server
      // has spawned us — guard anyway.
      let selfIdx = -1;
      for (let i = 0; i < msg.count; i++) {
        if (msg.ids[i] === msg.yourId) { selfIdx = i; break; }
      }
      if (selfIdx !== -1) {
        predictor.reconcile(
          { x: msg.xs[selfIdx]!, y: msg.ys[selfIdx]! },
          msg.lastInputTick,
        );
      }
      return;
    case "log":
      hud.workerLog = `[${msg.level}] ${msg.message}`;
      return;
    case "ping":
      // Application-level RTT measurement from the worker's text-frame
      // ping/pong. Already in milliseconds, already pure round-trip
      // (server echoes off the I/O thread before any tick processing).
      recordPing(msg.rttMs);
      return;
  }
};

worker.onerror = (err) => {
  hud.workerLog = `[worker-error] ${err.message}`;
};
